import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';

import { Role } from '@ndvr/auth-api';
import { PersonOrgFinancialProfile, PersonOrgType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { OrgDrawer, OrgType } from 'modules/OrgDrawer';
import { getOrgName, titleCase, useHouseholdRouteType } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import { CondensedTable, HorizontalLayout, Text, VerticalLayout } from 'widgets';

import { MemberActionsCell, useMemberLinkActions } from './MemberLinkActions';
import { TaxIdCell } from './TaxIdCell';

const columnHelper = createColumnHelper<PersonOrgFinancialProfile>();

export const OrgList = ({ profiles, orgType }: { profiles: PersonOrgFinancialProfile[]; orgType: OrgType }) => {
    const { data: ssnAccess } = useUserHasRole(Role.SSN_ACCESS);
    const { householdTypeLabel } = useHouseholdRouteType();
    const entities = useMemo(
        () => profiles.filter(({ profile }) => profile.personOrg.personOrgType === orgType),
        [orgType, profiles],
    );
    const orgTypeLabel = orgType === PersonOrgType.TRUST ? 'Trust' : 'Entity';

    const [showOrgCreateDrawer, setShowOrgCreateDrawer] = useState(false);
    const [orgToEdit, setOrgToEdit] = useState<PersonOrgFinancialProfile | undefined>(undefined);
    const { openLinkDrawer, requestUnlink, linkUnlinkNodes } = useMemberLinkActions({
        personOrgType: orgType,
        unlinkNoun: orgTypeLabel.toLowerCase(),
        getDisplayName: ({ profile }) => getOrgName(profile.personOrg),
    });

    const columns = [
        columnHelper.accessor(({ profile }) => getOrgName(profile.personOrg), {
            id: 'name',
            header: 'Name',
            cell: ({ getValue, row }) => <Text onClick={() => setOrgToEdit(row.original)}>{getValue()}</Text>,
            size: 1000,
        }),
        columnHelper.accessor(({ profile }) => profile.personOrg.personOrgSubtype, {
            id: 'type',
            header: 'Type',
            cell: ({ getValue }) => <Text>{titleCase(getValue()?.toLowerCase())}</Text>,
            size: 1000,
        }),
        ...(ssnAccess
            ? [
                  columnHelper.display({
                      id: 'taxID',
                      header: 'Tax ID',
                      cell: ({ row }) => (
                          <TaxIdCell
                              key={row.original.profile.personOrg.personOrgGUID}
                              personOrg={row.original.profile.personOrg}
                          />
                      ),
                      size: 200,
                  }),
              ]
            : []),
        columnHelper.display({
            id: 'actions',
            header: '',
            cell: ({ row }) => (
                <MemberActionsCell profile={row.original} onEdit={setOrgToEdit} onUnlink={requestUnlink} />
            ),
        }),
    ];

    return (
        <VerticalLayout $gap={8}>
            <HorizontalLayout $rowCenter $apart>
                <h2 className="text-cocoa-1000 text-xl">{orgType === PersonOrgType.TRUST ? 'Trusts' : 'Entities'}</h2>
                <HorizontalLayout $gap={16}>
                    <Button onClick={openLinkDrawer}>Link {orgTypeLabel}</Button>
                    <Button onClick={() => setShowOrgCreateDrawer(true)}>New {orgTypeLabel}</Button>
                </HorizontalLayout>
            </HorizontalLayout>
            {entities.length > 0 ? (
                <Card>
                    <CondensedTable data={entities} columns={columns} onRowClick={setOrgToEdit} />
                </Card>
            ) : (
                <Text $italic $themeColor="subtle">
                    There are no {orgType === PersonOrgType.TRUST ? 'Trusts' : 'Entities'} in this {householdTypeLabel}
                </Text>
            )}

            {linkUnlinkNodes}
            {showOrgCreateDrawer && <OrgDrawer onClose={() => setShowOrgCreateDrawer(false)} orgType={orgType} />}
            {orgToEdit && (
                <OrgDrawer
                    onClose={() => setOrgToEdit(undefined)}
                    orgType={orgType}
                    orgGUID={orgToEdit.profile.personOrgGUID}
                />
            )}
        </VerticalLayout>
    );
};
